import React, {
  useState,
  useRef,
  useContext,
  useEffect,
  useImperativeHandle,
} from "react";
import { auth } from "../firebase.config";
import { AuthContext } from "../context/AuthContext";
import { Mic, AudioWaveform as Waveform } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { ReactComponent as MicOn } from "../icons/mic_on.svg";
import { ReactComponent as MicOff } from "../icons/mic_off.svg";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

const SILENCE_THRESHOLD = -50; // dB
const SILENCE_DURATION = 1000; // 2 seconds in milliseconds

const MicrophoneButton = React.forwardRef(
  ({ onTranscriptionComplete, disabled }, ref) => {
    const buttonRef = useRef(null);
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);
    const mediaRecorderRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const silenceStartRef = useRef(null);
    const audioChunksRef = useRef([]);
    const { currentUser } = useContext(AuthContext);
    const animationFrameRef = useRef(null);

    const anonymousId = localStorage.getItem("anonymousId");

    useImperativeHandle(ref, () => ({
      triggerClick: () => {
        if (buttonRef.current) {
          buttonRef.current.click();
        }
      },
    }));

    useEffect(() => {
      return () => {
        console.log(
          "Cleanup: Canceling animation frame and closing AudioContext"
        );
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        if (
          audioContextRef.current &&
          audioContextRef.current.state !== "closed"
        ) {
          audioContextRef.current.close();
        }
      };
    }, []);

    const analyzeAudio = () => {
      if (!analyserRef.current) return;

      const dataArray = new Float32Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getFloatTimeDomainData(dataArray);

      // Calculate RMS value
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i] * dataArray[i];
      }
      const rms = Math.sqrt(sum / dataArray.length);
      const db = 20 * Math.log10(rms);

      setAudioLevel(Math.max(0, (db + 100) / 50)); // Normalize to 0-1

      // Check for silence
      if (db < SILENCE_THRESHOLD) {
        if (!silenceStartRef.current) {
          console.log("Silence detected, starting silence timer");
          silenceStartRef.current = Date.now();
        } else {
          const silenceDuration = Date.now() - silenceStartRef.current;
          // console.log(`Silence duration: ${silenceDuration}ms`);
          if (silenceDuration >= SILENCE_DURATION) {
            console.log("Silence threshold reached, stopping recording");
            stopRecording();
            return;
          }
        }
      } else {
        if (silenceStartRef.current) {
          console.log("Sound detected, resetting silence timer");
        }
        silenceStartRef.current = null;
      }

      console
        .log
        // `Audio level: ${db.toFixed(2)}dB, Normalized: ${audioLevel.toFixed(2)}`
        ();
      animationFrameRef.current = requestAnimationFrame(analyzeAudio);
    };

    const startRecording = async () => {
      try {
        console.log("Starting recording...");
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        console.log("Microphone access granted");

        // Set up audio analysis
        audioContextRef.current = new AudioContext();
        const source = audioContextRef.current.createMediaStreamSource(stream);
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048;
        source.connect(analyserRef.current);
        console.log("Audio analysis setup complete");

        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
            console.log(`Audio chunk received: ${event.data.size} bytes`);
          }
        };

        mediaRecorder.onstop = () => {
          console.log("MediaRecorder stopped, processing audio...");
          processAudio();
          stream.getTracks().forEach((track) => {
            track.stop();
            console.log("Audio track stopped");
          });
          if (
            audioContextRef.current &&
            audioContextRef.current.state !== "closed"
          ) {
            audioContextRef.current.close();
            console.log("AudioContext closed");
          }
        };

        mediaRecorder.start();
        setIsRecording(true);
        analyzeAudio();
        console.log("Recording started successfully");
      } catch (error) {
        console.error("Error accessing microphone:", error);
        alert(
          "Unable to access microphone. Please check your browser permissions."
        );
      }
    };

    const stopRecording = () => {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        setIsProcessing(true);
        silenceStartRef.current = null;
        if (animationFrameRef.current)
          cancelAnimationFrame(animationFrameRef.current);
      }
    };

    const processAudio = async () => {
      try {
        console.log("Processing audio data...");
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        console.log(`Audio blob created: ${audioBlob.size} bytes`);

        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);

        reader.onloadend = async () => {
          const base64Audio = reader.result;
          console.log("Audio converted to base64");

          try {
            const headers = {
              "Content-Type": "application/json",
            };

            const body = {
              audioData: base64Audio,
            };

            if (currentUser) {
              const token = await auth.currentUser.getIdToken();
              headers["Authorization"] = `Bearer ${token}`;
              console.log("Added authentication token to request");
            } else if (anonymousId) {
              body.anonymousId = anonymousId;
              console.log("Added anonymous ID to request");
            }

            console.log("Sending transcription request...");
            const response = await fetch(
              `${process.env.REACT_APP_API_URL || ""}/transcribe`,
              {
                method: "POST",
                headers: headers,
                body: JSON.stringify(body),
              }
            );

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.error(
                "Transcription API error:",
                response.status,
                errorData
              );
              throw new Error(`Failed to transcribe audio: ${response.status}`);
            }

            const data = await response.json();
            console.log("Transcription response received:", data);

            if (data.transcript && data.transcript.trim()) {
              console.log("Valid transcript received:", data.transcript);
              onTranscriptionComplete(data.transcript);

              setTimeout(() => {
                const submitButton = document.querySelector(
                  'form button[aria-label="form submit"]'
                );
                if (submitButton && !submitButton.disabled) {
                  console.log("Automatically submitting form");
                  submitButton.click();
                }
              }, 100);
            } else {
              console.warn("Empty transcript received");
            }
          } catch (error) {
            console.error("Error in fetch:", error);
          } finally {
            setIsProcessing(false);
            console.log("Processing completed");
          }
        };
      } catch (error) {
        console.error("Error processing audio:", error);
        setIsProcessing(false);
      }
    };

    return (
      <TooltipProvider>
        <Tooltip open={isRecording}>
          <TooltipTrigger asChild>
            <button
              ref={buttonRef}
              type="button"
              onClick={() => {
                if (isRecording) {
                  stopRecording();
                } else {
                  startRecording();
                }
              }}
              disabled={disabled || isProcessing}
              title={isRecording ? "Stop recording" : "Start voice recording"}
              className="relative transition-colors duration-200 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing || isRecording ? (
                <MicOff className="h-5 w-5" />
              ) : (
                <MicOn className=" h-5 w-5 text-gray-400" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Listening...</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);

export default MicrophoneButton;

export { MicrophoneButton };
